import React, {useEffect, useState} from 'react'
import {Helmet} from 'react-helmet-async'
import {LinkCrumbtrail} from '../components/crumbtrail'
import {ElapsedAbsoluteTime} from '../components/formatters'
import {
  PageActions,
  PageBody,
  PageDescription,
  PageHeader,
  PageHeaderContent
} from '../components/page-styles'
import {Button} from '../components/library/buttons'
import {
  List,
  NoResultsPermissions,
  makeLoadMoreUpdater
} from '../components/library/list'
import {Modal} from '../components/library/modal/modal'
import {Loader} from '../components/loader'
import {useWorkbenchActivityLogV2Query} from './generated/activity-log'
import {
  PermissionAction,
  PermissionResource
} from '../data/workbench-api/generated/types'
import {useAnalytics} from '../lib/analytics/analytics'
import {getLocalEnvironmentId} from '../lib/local-store'
import {OrganisationNode} from '../organisation'
import styled from 'styled-components'
import {gql} from '@apollo/client'

// FYI this isn't imported from generated as the work around we have
// for functions returning "setof" not working requiring us to return a table type
// causes all generated properties to be nullable
type WorkbenchActivityLogRecord = {
  id: string
  organisationId: string
  eventType: string
  payload: unknown
  created: string
}

const Clickable = styled.a`
  font-weight: bold;
  &:hover {
    text-decoration: underline;
  }
  &:focus {
    text-decoration: underline;
  }
`

const PAGE_LIMIT = 50

export const ActivityLog = ({
  organisation
}: {
  organisation: OrganisationNode
}) => {
  const analytics = useAnalytics()

  useEffect(() => {
    analytics.track({
      event: 'Viewed sdk request debugger',
      properties: {
        org_id: organisation.id,
        category: 'tools'
      }
    })
  }, [analytics, organisation.id])

  const canView = organisation.hasPermission(
    PermissionResource.ActivityLog,
    PermissionAction.View
  )
  const {data, refetch, loading, fetchMore} = useWorkbenchActivityLogV2Query({
    variables: {
      organisationId: organisation.id,
      resultLimit: PAGE_LIMIT
    }
  })
  const [showEvent, setShowEvent] =
    useState<WorkbenchActivityLogRecord | null>()
  const [loadingMore, setLoadingMore] = useState<boolean>(false)
  const [refreshing, setRefreshing] = useState<boolean>(false)

  const environmentId = getLocalEnvironmentId(organisation.id)

  // because the results come from a postgres function, the columns are nullable,
  // but we assume they are not null
  const rows =
    (data?.workbenchActivityLogV2?.nodes as WorkbenchActivityLogRecord[]) ?? []
  const lastId =
    data?.workbenchActivityLogV2?.nodes[
      data?.workbenchActivityLogV2?.nodes.length - 1
    ]?.id
  // This isn't flawless, as we could think there's more to fetch if the total is divisible by the limit
  // but it's fine for 99% of cases.
  const moreToFetch = rows.length % PAGE_LIMIT === 0

  return (
    <>
      <Helmet>
        <title>SDK request debugger</title>
      </Helmet>
      <PageHeader>
        {environmentId && (
          <LinkCrumbtrail
            crumbs={[[`../${environmentId}/tools`, 'Tools'], 'Configuration']}
          />
        )}
        <PageHeaderContent>
          <h2>SDK Request debugger</h2>
        </PageHeaderContent>
        <PageActions>
          <Button
            disabled={loading || loadingMore || refreshing || !canView}
            onClick={async () => {
              setRefreshing(true)
              await refetch()
              setRefreshing(false)
            }}
          >
            Refresh
          </Button>
        </PageActions>
      </PageHeader>
      <PageDescription>
        This screen shows failed client API requests, to help with debugging
        your SDK integration.
      </PageDescription>
      <PageBody data-testid={'request-debugger'}>
        {(!data && loading) || refreshing ? (
          <Loader />
        ) : (
          <List<WorkbenchActivityLogRecord>
            columns={[
              {
                id: 'timestamp',
                header: 'Timestamp',
                accessor: r => (
                  <Clickable onClick={() => setShowEvent(r)}>
                    <ElapsedAbsoluteTime ago={false} date={r.created} />
                  </Clickable>
                )
              },
              {
                id: 'type',
                header: 'Type',
                accessor: r => r.eventType
              },
              {
                id: 'error',
                header: 'Error',
                accessor: r =>
                  r.payload &&
                  typeof r.payload === 'object' &&
                  'error' in r.payload
                    ? (r.payload.error as string)
                    : undefined
              },
              {
                id: 'organisationId',
                header: 'Organization ID',
                accessor: r => r.organisationId
              },
              {
                id: 'id',
                horizontalAlign: 'right',
                accessor: r => (
                  <Button
                    appearance="secondary"
                    size="small"
                    onClick={() => setShowEvent(r)}
                  >
                    Detail
                  </Button>
                )
              }
            ]}
            rows={rows}
            loadingMore={loadingMore}
            fetchMore={
              moreToFetch && lastId && fetchMore
                ? async () => {
                    setLoadingMore(true)
                    await fetchMore({
                      variables: {
                        lastId
                      },
                      updateQuery: makeLoadMoreUpdater('workbenchActivityLogV2')
                    })
                    setLoadingMore(false)
                  }
                : undefined
            }
            renderEmpty={canView ? undefined : () => <NoResultsPermissions />}
          />
        )}
        {showEvent ? (
          <Modal title="Log detail" handleClose={() => setShowEvent(null)}>
            <pre
              style={{
                background: 'rgba(0, 0, 0, 0.05)',
                padding: '10px',
                borderRadius: '5px',
                overflow: 'auto'
              }}
            >
              {JSON.stringify(showEvent.payload, null, 2)}
            </pre>
          </Modal>
        ) : null}
      </PageBody>
    </>
  )
}

gql`
  query WorkbenchActivityLogV2(
    $organisationId: String!
    $resultLimit: Int!
    $lastId: String
  ) {
    workbenchActivityLogV2(
      organisationId: $organisationId
      resultLimit: $resultLimit
      lastId: $lastId
    ) {
      nodes {
        id
        organisationId
        eventType
        payload
        created
      }
      totalCount
    }
  }
`
